import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { Mail, Loader } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import EmailCampaignForm from './components/EmailCampaignForm';
import SignInModal from './components/SignInModal';

function App() {
  const [showSignIn, setShowSignIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial auth state
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        setShowSignIn(false);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      <div className="max-w-5xl mx-auto p-6">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
              <Mail className="h-8 w-8 mr-3 text-blue-500" />
              Email Campaign Manager
            </h1>
            <p className="text-gray-600">Create, customize, and send targeted email campaigns efficiently</p>
            <p className="text-sm text-gray-500 mt-2">Sending from: info@vantaprints.com</p>
          </div>
          <div>
            {user ? (
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">{user.email}</span>
                <button
                  onClick={() => supabase.auth.signOut()}
                  className="px-4 py-2 text-sm text-red-600 hover:text-red-700"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowSignIn(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Sign In with Google
              </button>
            )}
          </div>
        </div>
        {user ? (
          <EmailCampaignForm />
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Please Sign In</h2>
            <p className="text-gray-600 mb-6">Sign in with your Google account to access the email campaign manager.</p>
            <button
              onClick={() => setShowSignIn(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Sign In with Google
            </button>
          </div>
        )}
      </div>
      <SignInModal show={showSignIn} onClose={() => setShowSignIn(false)} />
    </div>
  );
}

export default App;